/**
 * Coordinación cross-tab del `device_id` (NIF-380).
 *
 * Si el mismo enlace se abre en dos pestañas a la vez (p. ej. preview de Outlook + clic),
 * sin coordinación cada pestaña calcularía un Thumbmark distinto. Este util garantiza un solo
 * cálculo por token del enlace y que el resto reutilice el valor de localStorage.
 */

/** Valor temporal en localStorage mientras la pestaña «líder» resuelve Thumbmark. */
export const DEVICE_ID_PENDING_MARKER = '__pending__';

const DEVICE_ID_POLL_INTERVAL_MS = 50;
const DEVICE_ID_PEER_WAIT_MAX_MS = 10_000;

export type CoordinatedDeviceIdResult<TFreshMeta> =
	| { origin: 'shared'; id: string }
	| { origin: 'fresh'; id: string; meta: TFreshMeta };

/**
 * Obtiene `device_id` desde localStorage compartido o lo crea una sola vez entre pestañas.
 *
 * @param storageKey Clave scoped al token del enlace (`pwcs_device_id:{hash}`).
 * @param createFreshId Solo la pestaña líder ejecuta esto (Thumbmark + fallback en AuthService).
 */
export async function coordinateDeviceIdResolution<TFreshMeta>(
	storageKey: string,
	createFreshId: () => Promise<{ id: string; meta: TFreshMeta }>,
): Promise<CoordinatedDeviceIdResult<TFreshMeta>> {
	const cached = await readResolvedDeviceIdOrWaitForPeer(storageKey);
	if (cached) return { origin: 'shared', id: cached };

	return withDeviceIdResolveLock(storageKey, async () => {
		const afterLock = await readResolvedDeviceIdOrWaitForPeer(storageKey);
		if (afterLock) return { origin: 'shared', id: afterLock };

		markDeviceIdPending(storageKey);
		try {
			const { id, meta } = await createFreshId();
			persistDeviceId(storageKey, id);
			return { origin: 'fresh', id, meta };
		} catch (error) {
			clearDeviceIdIfPending(storageKey);
			throw error;
		}
	});
}

/** Lee el id persistido; si otra pestaña está resolviendo, espera hasta {@link DEVICE_ID_PEER_WAIT_MAX_MS}. */
async function readResolvedDeviceIdOrWaitForPeer(storageKey: string): Promise<string | null> {
	const stored = readLocalStorage(storageKey);
	if (!stored) return null;
	if (stored !== DEVICE_ID_PENDING_MARKER) return stored;
	return waitForPeerDeviceIdResolution(storageKey);
}

/** Marca la clave como «en resolución» para que otras pestañas hagan poll en lugar de duplicar Thumbmark. */
function markDeviceIdPending(storageKey: string): void {
	try {
		if (!localStorage.getItem(storageKey)) {
			localStorage.setItem(storageKey, DEVICE_ID_PENDING_MARKER);
		}
	} catch {
		// localStorage no disponible (modo privado estricto, iframe, etc.)
	}
}

/** Limpia el marcador si Thumbmark falló, para no dejar a las demás pestañas esperando indefinidamente. */
function clearDeviceIdIfPending(storageKey: string): void {
	try {
		if (localStorage.getItem(storageKey) === DEVICE_ID_PENDING_MARKER) {
			localStorage.removeItem(storageKey);
		}
	} catch {
		// localStorage no disponible
	}
}

/** Poll en localStorage hasta que la pestaña líder sustituya `__pending__` por el id real. */
async function waitForPeerDeviceIdResolution(storageKey: string): Promise<string | null> {
	const deadline = Date.now() + DEVICE_ID_PEER_WAIT_MAX_MS;
	while (Date.now() < deadline) {
		const value = readLocalStorage(storageKey);
		if (value && value !== DEVICE_ID_PENDING_MARKER) return value;
		await new Promise((resolve) => setTimeout(resolve, DEVICE_ID_POLL_INTERVAL_MS));
	}
	return null;
}

/**
 * Serializa la resolución entre pestañas.
 * - Con `navigator.locks`: una sola pestaña ejecuta `fn` (API preferida).
 * - Sin locks: fallback con marcador `__pending__` + polling.
 */
async function withDeviceIdResolveLock<T>(storageKey: string, fn: () => Promise<T>): Promise<T> {
	const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
	if (locks?.request) {
		return locks.request(`pwcs:device-id:${storageKey}`, fn);
	}

	const existing = readLocalStorage(storageKey);
	if (existing === DEVICE_ID_PENDING_MARKER) {
		const peer = await waitForPeerDeviceIdResolution(storageKey);
		if (peer) return { origin: 'shared', id: peer } as T;
	}

	return fn();
}

function readLocalStorage(key: string): string | null {
	try {
		const value = localStorage.getItem(key)?.trim();
		return value || null;
	} catch {
		return null;
	}
}

function persistDeviceId(storageKey: string, deviceId: string): void {
	try {
		localStorage.setItem(storageKey, deviceId);
	} catch {
		// Sin persistencia si localStorage no está disponible
	}
}
